import React from 'react';
import { Clock, Calendar } from 'lucide-react';
import { BlogPost } from '../../types/blog';

interface BlogCardProps {
  post: BlogPost;
  onClick: () => void;
}

export const BlogCard: React.FC<BlogCardProps> = ({ post, onClick }) => {
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <article 
      className="bg-gray-900/50 rounded-lg overflow-hidden border border-gray-800 hover:border-green-400/50 transition-all duration-300 cursor-pointer group backdrop-blur-sm"
      onClick={onClick}
    >
      <div className="relative aspect-[5/4] bg-gray-800 overflow-hidden">
        <picture>
          <source 
            srcSet={post.previewImageWebp || post.image} 
            type="image/webp" 
          />
          <img 
            src={post.previewImageJpg || post.image} 
            alt={post.title}
            className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105 bg-gray-800"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = 'https://images.unsplash.com/photo-1639322537228-f710d846310a?auto=format&fit=crop&w=800&h=400';
            }}
          />
        </picture>
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
      </div>
      
      <div className="p-6">
        <h3 className="text-xl font-semibold text-white mb-3 line-clamp-2 group-hover:text-green-400 transition-colors duration-300">
          {post.title}
        </h3>
        
        <p className="text-gray-400 text-sm mb-4 line-clamp-3 leading-relaxed">
          {post.hook}
        </p>
        
        <div className="flex items-center justify-between text-xs text-gray-500">
          <div className="flex items-center gap-2">
            <Calendar size={14} />
            <time dateTime={post.publishedAt}>
              {formatDate(post.publishedAt)}
            </time>
          </div>
          
          <div className="flex items-center gap-2">
            <Clock size={14} />
            <span>{post.readingTime} min read</span>
          </div>
        </div>
      </div>
    </article>
  );
};